import { ForumComment, ForumPost } from '@/lib/forumData';
import {
  createForumCommentRequest,
  createForumPostRequest,
  deleteForumCommentRequest,
  deleteForumPostRequest,
  getForumPosts,
  getUserForumPosts,
  getForumPostsServer,
} from '@/lib/api/forum';
import {
  clearStoredForumSession,
  FORUM_POSTS_CACHE_KEY,
  FORUM_SESSION_EVENT,
  FORUM_SESSION_KEY,
  notifyForumSessionChange,
} from '@/lib/forumSession';

export type ForumSession = {
  id?: string | number;
  username?: string;
  firstName: string;
  lastName: string;
  email: string;
  token?: string;
  profilePic?: string;
};

type BackendForumComment = {
  id: string | number;
  user_id?: string | number;
  text: string;
  date: string;
  post_id: string | number;
  first_name?: string;
  last_name?: string;
  username?: string;
  author_name?: string;
};

type BackendForumPost = {
  id: string | number;
  user_id?: string | number;
  header: string;
  text: string;
  date: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  author_name?: string;
  comments?: BackendForumComment[];
};

export { FORUM_SESSION_EVENT } from '@/lib/forumSession';
let cachedForumSessionRaw: string | null | undefined;
let cachedForumSessionParsed: ForumSession | null = null;

const isBrowser = () => typeof window !== 'undefined';

const readJson = <T,>(key: string, fallback: T): T => {
  if (!isBrowser()) return fallback;

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key: string, value: unknown) => {
  if (!isBrowser()) return;
  window.localStorage.setItem(key, JSON.stringify(value));
};

const formatBackendDate = (value?: string) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const getAuthorName = ({
  firstName,
  lastName,
  username,
  authorName,
}: {
  firstName?: string;
  lastName?: string;
  username?: string;
  authorName?: string;
}) =>
  authorName ||
  `${firstName || ''} ${lastName || ''}`.trim() ||
  username ||
  'Unknown User';

const mapComment = (comment: BackendForumComment): ForumComment => ({
  id: String(comment.id),
  userId: comment.user_id ? String(comment.user_id) : undefined,
  author: getAuthorName({
    firstName: comment.first_name,
    lastName: comment.last_name,
    username: comment.username,
    authorName: comment.author_name,
  }),
  date: formatBackendDate(comment.date),
  body: comment.text,
});

const mapPost = (post: BackendForumPost): ForumPost => ({
  slug: String(post.id),
  userId: post.user_id ? String(post.user_id) : undefined,
  author: getAuthorName({
    firstName: post.first_name,
    lastName: post.last_name,
    username: post.username,
    authorName: post.author_name,
  }),
  date: formatBackendDate(post.date),
  title: post.header,
  body: post.text
    .split(/\n+/)
    .map((part) => part.trim())
    .filter(Boolean),
  comments: (post.comments || []).map(mapComment),
});

export const getForumSession = () => {
  if (!isBrowser()) return null;

  const raw = window.localStorage.getItem(FORUM_SESSION_KEY);

  if (raw === cachedForumSessionRaw) {
    return cachedForumSessionParsed;
  }

  cachedForumSessionRaw = raw;

  if (!raw) {
    cachedForumSessionParsed = null;
    return cachedForumSessionParsed;
  }

  try {
    cachedForumSessionParsed = JSON.parse(raw) as ForumSession;
  } catch {
    cachedForumSessionParsed = null;
  }

  return cachedForumSessionParsed;
};

export const saveForumSession = (session: ForumSession) => {
  writeJson(FORUM_SESSION_KEY, session);
  cachedForumSessionRaw = JSON.stringify(session);
  cachedForumSessionParsed = session;
  notifyForumSessionChange();
};

export const clearForumSession = () => {
  if (!isBrowser()) return;
  cachedForumSessionRaw = null;
  cachedForumSessionParsed = null;
  clearStoredForumSession();
};

export const subscribeForumSession = (callback: () => void) => {
  if (!isBrowser()) {
    return () => {};
  }

  window.addEventListener(FORUM_SESSION_EVENT, callback);
  window.addEventListener('storage', callback);

  return () => {
    window.removeEventListener(FORUM_SESSION_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
};

export const getForumSessionServerSnapshot = () => null;

export const getUserDisplayName = (session: ForumSession | null) => {
  if (!session) return '';
  return (
    `${session.firstName} ${session.lastName}`.trim() ||
    session.username ||
    session.email
  );
};

export const cacheForumPosts = (posts: ForumPost[]) => {
  writeJson(FORUM_POSTS_CACHE_KEY, posts);
};

export const getCachedForumPosts = () =>
  readJson<ForumPost[]>(FORUM_POSTS_CACHE_KEY, []);

export const getCachedForumPostBySlug = (slug: string) =>
  getCachedForumPosts().find((post) => post.slug === slug) || null;

export const fetchForumPosts = async (): Promise<ForumPost[]> => {
  const payload = await getForumPosts();
  const posts = ((payload?.data || []) as BackendForumPost[]).map(mapPost);
  cacheForumPosts(posts);
  return posts;
};

export const fetchForumPostBySlug = async (slug: string) => {
  const posts = await fetchForumPosts();
  return posts.find((post) => post.slug === slug) || null;
};

export const fetchUserForumPosts = async (userId: string): Promise<ForumPost[]> => {
  const payload = await getUserForumPosts(userId);
  return ((payload?.data || []) as BackendForumPost[]).map(mapPost);
};

export const fetchForumPostsServer = async (): Promise<ForumPost[]> => {
  const payload = await getForumPostsServer();
  return ((payload?.data || []) as BackendForumPost[]).map(mapPost);
};

export const fetchForumPostBySlugServer = async (slug: string) => {
  const posts = await fetchForumPostsServer();
  return posts.find((post) => post.slug === slug) || null;
};

export const createForumPost = async ({
  title,
  body,
  session,
}: {
  title: string;
  body: string;
  session: ForumSession;
}) => {
  const payload = await createForumPostRequest({
    header: title,
    text: body,
  });

  const created = payload?.data as BackendForumPost | undefined;
  return mapPost({
    ...(created || {
      id: Date.now(),
      header: title.trim(),
      text: body.trim(),
      date: new Date().toISOString(),
    }),
    first_name: created?.first_name || session.firstName,
    last_name: created?.last_name || session.lastName,
    comments: created?.comments || [],
  });
};

export const createForumComment = async ({
  slug,
  body,
  session,
}: {
  slug: string;
  body: string;
  session: ForumSession;
}) => {
  const payload = await createForumCommentRequest({
    postId: slug,
    text: body,
  });

  const created = payload?.data as BackendForumComment | undefined;
  return mapComment({
    ...(created || {
      id: Date.now(),
      text: body.trim(),
      date: new Date().toISOString(),
      post_id: slug,
    }),
    first_name: created?.first_name || session.firstName,
    last_name: created?.last_name || session.lastName,
  });
};

export const deleteForumPost = async (postId: string) => {
  await deleteForumPostRequest(postId);
  return true;
};

export const deleteForumComment = async (commentId: string) => {
  await deleteForumCommentRequest(commentId);
  return true;
};
